import { GameweekOpsData, GoalsPrefillSource, StandingsPrefillSource, LeagueTableRow } from "../../api/admin";

export interface PrefillStatRow {
  playerId: string;
  playerName: string;
  goals: number;
  assists: number;
  yellowCards: number;
  redCards: number;
}

export interface PrefillStandingRow {
  clubId: string;
  position: number;
}

export interface GameweekPrefillState {
  notCompleted: boolean;
  proposalStats: { playerId: string; playerName: string; goals: number; assists: number; yellowCards?: number; redCards?: number }[];
  statRows: PrefillStatRow[];
  standingRows: PrefillStandingRow[];
  goalsThisRound: number | "";
  statsPrefilled: boolean;
  goalsPrefilled: boolean;
  standingsSource: StandingsPrefillSource | null;
  standingsTable: LeagueTableRow[] | null;
  goalsSource: GoalsPrefillSource | null;
  statsProvisional: boolean;
  goalsProvisional: boolean;
  canApplyProposal: boolean;
  coverageEmpty: { eventRows: number; matched: number; unknownElement: number; unmatchedClub: number; unmatchedPlayer: number } | null;
}

function alphabeticalStandings(clubs: { id: string; name: string }[]): PrefillStandingRow[] {
  return [...clubs].sort((a, b) => a.name.localeCompare(b.name)).map((c, i) => ({ clubId: c.id, position: i + 1 }));
}

// `existing` is non-null when EITHER stats or standings have been submitted (an OR), so gating on
// the whole object would skip prefilling one field just because the other already has submitted
// data. Gate each field on its own array, and additionally never prefill over a completed
// gameweek — completedAt is the one signal that survives even when a field's stored value happens
// to look "empty" (e.g. goalsThisRound genuinely 0).
export function resolveGameweekPrefill(data: GameweekOpsData): GameweekPrefillState {
  const existingStats = data.existing?.stats ?? [];
  const existingStandings = data.existing?.standings ?? [];
  const notCompleted = data.gameweek.completedAt == null;
  const proposalStats = data.proposal?.status === "READY" ? data.proposal.payload.stats : [];

  const statsPrefilled = existingStats.length === 0 && notCompleted && proposalStats.length > 0;
  const goalsPrefilled = data.gameweek.goalsThisRound == null && notCompleted && data.goalsPrefill.goals != null;
  const standingsSource = existingStandings.length > 0 ? null : data.standingsPrefill.source;
  // Only meaningful alongside a DERIVED source — PREVIOUS/NONE never carry `table`, and once
  // standings are already submitted the admin is editing stored positions, not a fresh derivation.
  const standingsTable = existingStandings.length > 0 ? null : data.standingsPrefill.table;
  const goalsSource: GoalsPrefillSource | null = goalsPrefilled ? data.goalsPrefill.source : null;
  const canApplyProposal = data.proposal?.status === "READY" && existingStats.length > 0 && proposalStats.length > 0;

  // Staging (Step 1) deliberately happens before FPL confirms its stats (data_checked), so a
  // freshly-staged proposal's numbers can still move. Fixture-derived goals aren't provisional in
  // this sense — they come from our own Fixture.status, not FPL's live per-player stats — so this
  // only ever qualifies the player-stats prefill and a proposal-sourced goals fallback.
  const proposalProvisional = data.proposal?.status === "READY" && !(data.proposal.payload.dataChecked ?? false);
  const statsProvisional = statsPrefilled && proposalProvisional;
  const goalsProvisional = goalsPrefilled && goalsSource === "PROPOSAL" && proposalProvisional;

  // A READY proposal with 0 matched rows despite FPL reporting events this round means every
  // scorer failed to resolve to this competition's club/player mapping — indistinguishable from a
  // genuinely quiet gameweek unless coverage says otherwise. Absent on proposals staged before
  // this field shipped, so this only ever adds a warning, never suppresses information.
  const coverage = data.proposal?.status === "READY" ? data.proposal.payload.coverage : undefined;
  const coverageEmpty = proposalStats.length === 0 && coverage != null && coverage.eventRows > 0 ? coverage : null;

  const statRows: PrefillStatRow[] = existingStats.length > 0
    ? existingStats
    : statsPrefilled
      ? proposalStats.map((s) => ({ playerId: s.playerId, playerName: s.playerName, goals: s.goals, assists: s.assists, yellowCards: s.yellowCards ?? 0, redCards: s.redCards ?? 0 }))
      : [];

  const standingRows: PrefillStandingRow[] = existingStandings.length > 0
    ? existingStandings
    : (data.standingsPrefill.standings.length > 0 ? data.standingsPrefill.standings : alphabeticalStandings(data.competition.clubs));

  const goalsThisRound: number | "" = data.gameweek.goalsThisRound != null
    ? data.gameweek.goalsThisRound
    : (goalsPrefilled ? (data.goalsPrefill.goals as number) : "");

  return {
    notCompleted, proposalStats, statRows, standingRows, goalsThisRound,
    statsPrefilled, goalsPrefilled, standingsSource, standingsTable, goalsSource,
    statsProvisional, goalsProvisional, canApplyProposal, coverageEmpty,
  };
}
